import * as MedicalDataTypes from './MedicalDataTypes';
import { NoteEntityType, IPLocation, ParsedUserAgent } from './DynamoTypes';
import { ReportType } from './reports';

export type ReferencedName = {
    preferredFirstName?: string;
    firstName: string;
    lastName: string;
};

export type ReferencedDescription = {
    description: string;
};

export type ReferencedResource = {
    description: string;
    category: MedicalDataTypes.PrimaryRecordResourceCategory;
    providerName?: string;
    dateTime?: string;
};

export type ReferencedNote = {
    noteTitle: string;
    noteEntityType: NoteEntityType;
    noteResourceType?: MedicalDataTypes.PrimaryRecordResourceCategory;
    noteResource?: string;
};

export type ReferencedNoteComment = {
    noteComment: string;
};

export type ReferencedOneUpHealthConnectedHealthSystem = {
    connectedHealthSystem: string;
};

export type ReferencedOneUpHealthSystem = {
    healthSystemId: string;
    healthSystemName: string;
};

export type ReferencedSharedReport = {
    sharedReportType: ReportType;
};

export type ReferencedSharedReportView = {
    ipLocation: IPLocation | null;
    parsedUserAgent: ParsedUserAgent | null;
};

export type ReferencedRole = {
    name: 'owner' | 'admin' | 'agent';
};

export type ReferencedOrganization = {
    name: string;
};

export type ReferencedMedicalProfileFile = {
    fileName: string;
};

export type ReferencedOrganizationInvoice = {
    description?: string;
    invoiceNumber: string;
    amount: number;
};

export type ReferencedOrganizationGroup = {
    name: string;
};

export type AudiLogReferenceValue<TResolvedReference> = {
    id: string;
    references: TResolvedReference;
};

export type SingleAuditLogReferenceValue<TResolvedReference> = AudiLogReferenceValue<TResolvedReference>;
export type ListAuditLogReferenceValue<TResolvedReference> = AudiLogReferenceValue<TResolvedReference>[];
export type SingleOrListAuditLogReferenceValue<TResolvedReference> =
    | SingleAuditLogReferenceValue<TResolvedReference>
    | ListAuditLogReferenceValue<TResolvedReference>;

export type AudiLogReference<TKey extends string, TResolvedReference> = {
    [key in TKey]: SingleOrListAuditLogReferenceValue<TResolvedReference>;
};

export type AuditLogValue<TKey extends string, TValue extends string> = {
    [key in TKey]: TValue;
};

export type UnresolvedAuditLogReference<T extends AudiLogReference<any, any> | AuditLogValue<any, any>> = {
    [key in keyof T]: T[key] extends string | number | boolean ? T[key] | T[key][] : { id: string } | { id: string }[];
};

export type UserReference = AudiLogReference<'user', ReferencedName>;
export type ProfileReference = AudiLogReference<'profile', ReferencedName>;
export type CollaboratorReference = AudiLogReference<'collaborator', ReferencedName>;
export type CollaboratorInviteeReference = AudiLogReference<'collaboratorInvitee', ReferencedName>;
export type CollaboratorUserId = AudiLogReference<'collaboratorUserId', ReferencedName>;
export type CareTeamMemberReference = AudiLogReference<'careTeamMember', ReferencedDescription>;
export type OrganizationCareTeamMemberReference = AudiLogReference<'organizationCareTeamMember', ReferencedDescription>;
export type ResourceReference = AudiLogReference<'resource', ReferencedResource>;
export type NoteReference = AudiLogReference<'note', ReferencedNote>;
export type NoteCommentReference = AudiLogReference<'noteComment', ReferencedNoteComment>;
export type OneUpHealthConnectedHealthSystemReference = AudiLogReference<
    'oneUpHealthConnectedHealthSystem',
    ReferencedOneUpHealthConnectedHealthSystem
>;
export type OneUpHealthHealthSystemReference = AudiLogReference<'oneUpHealthSystem', ReferencedOneUpHealthSystem>;
export type SharedReportReference = AudiLogReference<'sharedReport', ReferencedSharedReport>;
export type SharedReportViewReference = AudiLogReference<'sharedReportView', ReferencedSharedReportView>;
export type OrganizationReference = AudiLogReference<'organization', ReferencedOrganization>;
export type UserOrganizationReference = AudiLogReference<'userOrganization', ReferencedOrganization>;
export type OptionalUserOrganizationReference = Partial<UserOrganizationReference>;
export type MedicalProfileFileReference = AudiLogReference<'medicalProfileFile', ReferencedMedicalProfileFile>;
export type OrganizationInvoiceReference = AudiLogReference<'organizationInvoice', ReferencedOrganizationInvoice>;
export type OrganizationGroupReference = AudiLogReference<'organizationGroup', ReferencedOrganizationGroup>;
export type DirectiveReference = AudiLogReference<'directive', NonNullable<unknown>>;
export type RoleReference = AudiLogReference<'role', ReferencedRole>;

export type ReferencedFastenConnection = {
    institutionName: string;
};

export type FastenConnectionReference = AudiLogReference<'fastenConnection', ReferencedFastenConnection>;
export type OptionalFastenConnectionReference = Partial<FastenConnectionReference>;

export type MessageValue = AuditLogValue<'message', string>;

export type Reference =
    | UserReference
    | RoleReference
    | ProfileReference
    | CollaboratorReference
    | CollaboratorInviteeReference
    | CollaboratorUserId
    | CareTeamMemberReference
    | OrganizationCareTeamMemberReference
    | ResourceReference
    | NoteReference
    | NoteCommentReference
    | OneUpHealthConnectedHealthSystemReference
    | OneUpHealthHealthSystemReference
    | SharedReportReference
    | SharedReportViewReference
    | OrganizationReference
    | UserOrganizationReference
    | MedicalProfileFileReference
    | OrganizationInvoiceReference
    | OrganizationGroupReference
    | DirectiveReference
    | FastenConnectionReference;

type MergeAuditLogReferences<T extends (AudiLogReference<any, any> | AuditLogValue<any, any>)[]> = T extends [
    infer First,
    ...infer Rest,
]
    ? Rest extends [] // Check if there are no more types left to merge
        ? First // If no more types, return the last type
        : Rest extends (AudiLogReference<any, any> | AuditLogValue<any, any>)[]
          ? First & MergeAuditLogReferences<Rest>
          : First
    : Record<string, never>;

export type UnresolvedAuditLog<TAuditLog extends BaseAuditLogType<any, any>> = {
    type: TAuditLog['type'];
    references: {
        [key in keyof TAuditLog['references']]: TAuditLog['references'][key] extends string | number | boolean
            ? TAuditLog['references'][key] | TAuditLog['references'][key][]
            :
                  | {
                        id: string;
                    }
                  | { id: string }[];
    };
};

/**
 * A single audit log of a predefined category/type
 */
type BaseAuditLogType<
    TType extends string,
    TAuditLogValue extends (AudiLogReference<any, any> | AuditLogValue<any, any>)[],
> = {
    type: TType;
    references: MergeAuditLogReferences<TAuditLogValue>;
};

export type AuditLogWithReference<TAuditLogReference extends AudiLogReference<any, any>> = BaseAuditLogType<
    any,
    [TAuditLogReference]
>;

/**
 * User registers initial profile information
 */
export type UserRegistrationLogMetadata = BaseAuditLogType<'user-registration', [UserReference]>;

/**
 * User has viewed a profile
 */
export type UserProfileViewLogMetadata = BaseAuditLogType<'user-profile-view', [UserReference, ProfileReference]>;

export type MedicalProfileCreationLogMetadata = BaseAuditLogType<
    'medical-profile-creation',
    [UserReference, ProfileReference]
>;

export type MedicalProfileDeletionLogMetadata = BaseAuditLogType<
    'medical-profile-deletion',
    [UserReference, ProfileReference]
>;

export type MedicalProfileOwnershipTransferLogMetadata = BaseAuditLogType<
    'medical-profile-ownership-transfer',
    [UserReference, ProfileReference]
>;

export type CollaboratorAddLogMetadata = BaseAuditLogType<
    'collaborator-add',
    [UserReference, ProfileReference, CollaboratorReference]
>;

export type CollaboratorRemoveLogMetadata = BaseAuditLogType<
    'collaborator-remove',
    [UserReference, ProfileReference, CollaboratorReference]
>;

export type CollaboratorInviteLogMetadata = BaseAuditLogType<
    'collaborator-invite',
    [UserReference, ProfileReference, CollaboratorInviteeReference]
>;

export type CollaboratorInviteAcceptLogMetadata = BaseAuditLogType<
    'collaborator-invite-accept',
    [UserReference, ProfileReference, CollaboratorUserId, CollaboratorReference]
>;

export type CollaboratorInviteDeclineLogMetadata = BaseAuditLogType<
    'collaborator-invite-decline',
    [UserReference, ProfileReference, CollaboratorInviteeReference]
>;

export type CareTeamMemberCollaboratorAddLogMetadata = BaseAuditLogType<
    'care-team-member-collaborator-add',
    [UserReference, ProfileReference, CareTeamMemberReference, CollaboratorReference]
>;

export type CareTeamMemberCollaboratorRemoveLogMetadata = BaseAuditLogType<
    'care-team-member-collaborator-remove',
    [UserReference, ProfileReference, CareTeamMemberReference, CollaboratorReference]
>;

export type ResourceCreationLogMetadata = BaseAuditLogType<
    'resource-creation',
    [UserReference, ProfileReference, ResourceReference, OptionalUserOrganizationReference]
>;

export type ResourceUpdateLogMetadata = BaseAuditLogType<
    'resource-update',
    [UserReference, ProfileReference, ResourceReference, OptionalUserOrganizationReference]
>;

export type ResourceDeletionLogMetadata = BaseAuditLogType<
    'resource-delete',
    [UserReference, ProfileReference, ResourceReference, OptionalUserOrganizationReference]
>;

export type NoteCreationLogMetadata = BaseAuditLogType<
    'note-creation',
    [UserReference, ProfileReference, NoteReference, OptionalUserOrganizationReference]
>;

export type NoteMoveLogMetadata = BaseAuditLogType<
    'note-move',
    [UserReference, ProfileReference, NoteReference, OptionalUserOrganizationReference]
>;

export type NoteCommentLogMetadata = BaseAuditLogType<
    'note-comment',
    [UserReference, ProfileReference, NoteReference, NoteCommentReference, OptionalUserOrganizationReference]
>;

export type OneUpIntegrationAddLogMetadata = BaseAuditLogType<
    '1up-integration-add',
    [UserReference, ProfileReference, OneUpHealthConnectedHealthSystemReference, OptionalUserOrganizationReference]
>;

export type OneUpIntegrationRefreshLogMetadata = BaseAuditLogType<
    '1up-integration-refresh',
    [UserReference, ProfileReference, OneUpHealthConnectedHealthSystemReference, OptionalUserOrganizationReference]
>;

export type OneUpIntegrationRemoveLogMetadata = BaseAuditLogType<
    '1up-integration-remove',
    [UserReference, ProfileReference, OneUpHealthHealthSystemReference, OptionalUserOrganizationReference]
>;

export type FastenIntegrationAddLogMetadata = BaseAuditLogType<
    'fasten-integration-add',
    [UserReference, ProfileReference, FastenConnectionReference]
>;

export type FastenIntegrationRemoveLogMetadata = BaseAuditLogType<
    'fasten-integration-remove',
    [UserReference, ProfileReference, FastenConnectionReference]
>;

export type FastenIntegrationRefreshLogMetadata = BaseAuditLogType<
    'fasten-integration-refresh',
    [UserReference, ProfileReference, FastenConnectionReference]
>;

export type SharedReportCreationLogMetadata = BaseAuditLogType<
    'shared-report-creation',
    [UserReference, ProfileReference, SharedReportReference, OptionalUserOrganizationReference]
>;

export type SharedReportDeletionLogMetadata = BaseAuditLogType<
    'shared-report-deletion',
    [UserReference, ProfileReference, SharedReportReference, OptionalUserOrganizationReference]
>;

export type SharedReportViewedLogMetadata = BaseAuditLogType<
    'shared-report-viewed',
    [ProfileReference, SharedReportReference, SharedReportViewReference]
>;

export type ProfileMessageLogMetadata = BaseAuditLogType<
    'profile-message',
    [ProfileReference, UserReference, MessageValue, OptionalUserOrganizationReference]
>;

export type PortalRefreshCompletionLogMetadata = BaseAuditLogType<
    'portal-refresh-completion',
    [ProfileReference, OptionalFastenConnectionReference]
>;

export type MedicalProfileFileUploadLogMetadata = BaseAuditLogType<
    'medical-profile-file-upload',
    [UserReference, ProfileReference, MedicalProfileFileReference, OptionalUserOrganizationReference]
>;

export type MedicalProfileFileDeleteLogMetadata = BaseAuditLogType<
    'medical-profile-file-delete',
    [UserReference, ProfileReference, MedicalProfileFileReference, OptionalUserOrganizationReference]
>;

export type CodeStatusDirectiveCreationLogMetadata = BaseAuditLogType<
    'code-status-directive-creation',
    [DirectiveReference, UserReference, ProfileReference, OptionalUserOrganizationReference]
>;

export type CodeStatusDirectiveUpdateLogMetadata = BaseAuditLogType<
    'code-status-directive-update',
    [DirectiveReference, UserReference, ProfileReference, OptionalUserOrganizationReference]
>;

export type POADirectiveCreationLogMetadata = BaseAuditLogType<
    'poa-directive-creation',
    [DirectiveReference, UserReference, ProfileReference, OptionalUserOrganizationReference]
>;

export type POADirectiveUpdateLogMetadata = BaseAuditLogType<
    'poa-directive-update',
    [DirectiveReference, UserReference, ProfileReference, OptionalUserOrganizationReference]
>;

export type NoKnownAllergiesIndicatedLogMetadata = BaseAuditLogType<
    'no-known-allergies-indicated',
    [UserReference, ProfileReference]
>;

export type OrganizationInvoiceCreationLogMetadata = BaseAuditLogType<
    'organization-invoice-creation',
    [
        ProfileReference,
        UserReference,
        OrganizationReference,
        OrganizationInvoiceReference,
        OptionalUserOrganizationReference,
    ]
>;

export type OrganizationCollaboratorAddLogMetadata = BaseAuditLogType<
    'organization-collaborator-add',
    [OrganizationReference, ProfileReference]
>;

export type OrganizationCollaboratorRemoveLogMetadata = BaseAuditLogType<
    'organization-collaborator-remove',
    [OrganizationReference, ProfileReference]
>;

// Org Entity Audit Log Events
export type OrganizationPubliclyVisibleStatusUpdateLogMetadata = BaseAuditLogType<
    'organization-publicly-visible-status-update',
    [UserReference, OrganizationReference]
>;

export type OrganizationSessionTimeoutUpdateLogMetadata = BaseAuditLogType<
    'organization-session-timeout-update',
    [UserReference, OrganizationReference]
>;

export type OrganizationPaidStatusUpdateLogMetadata = BaseAuditLogType<
    'organization-paid-status-update',
    [UserReference, OrganizationReference]
>;

export type OrgUserCreationLogMetadata = BaseAuditLogType<
    'organization-user-creation',
    [UserReference, OrganizationReference]
>;

export type OrgUserCreationOnInviteAcceptLogMetadata = BaseAuditLogType<
    'organization-user-creation-on-invite-accept',
    [UserReference, OrganizationReference]
>;

export type OrgOwnerRoleAssignmentLogMetadata = BaseAuditLogType<
    'organization-owner-role-assignment',
    [UserReference, RoleReference, OrganizationReference]
>;

export type OrgUserRoleAssignmentLogMetadata = BaseAuditLogType<
    'organization-user-role-assignment',
    [UserReference, RoleReference, OrganizationReference]
>;

export type OrgUserUpdateLogMetadata = BaseAuditLogType<
    'organization-user-update',
    [UserReference, OrganizationReference]
>;

export type OrgUserDeleteLogMetadata = BaseAuditLogType<
    'organization-user-delete',
    [UserReference, OrganizationReference]
>;

export type OrgUserGroupAddLogMetadata = BaseAuditLogType<
    'organization-user-group-add',
    [UserReference, OrganizationReference, UserOrganizationReference, OrganizationGroupReference]
>;

export type OrgUserGroupRemoveLogMetadata = BaseAuditLogType<
    'organization-user-group-remove',
    [UserReference, OrganizationReference, UserOrganizationReference, OrganizationGroupReference]
>;

export type OrgUserGroupAddBulkLogMetadata = BaseAuditLogType<
    'organization-user-group-add-bulk',
    [UserReference, OrganizationReference, UserOrganizationReference, OrganizationGroupReference]
>;

export type OrgUserGroupRemoveBulkLogMetadata = BaseAuditLogType<
    'organization-user-group-remove-bulk',
    [UserReference, OrganizationReference, UserOrganizationReference, OrganizationGroupReference]
>;

export type OrgCareTeamMemberAddLogMetadata = BaseAuditLogType<
    'organization-care-team-member-add',
    [UserReference, OrganizationCareTeamMemberReference, OrganizationReference]
>;

export type OrgCareTeamMemberUpdateLogMetadata = BaseAuditLogType<
    'organization-care-team-member-update',
    [UserReference, OrganizationCareTeamMemberReference, OrganizationReference]
>;

export type OrgCareTeamMemberDeleteLogMetadata = BaseAuditLogType<
    'organization-care-team-member-delete',
    [UserReference, OrganizationCareTeamMemberReference, OrganizationReference]
>;

export type OrgNoteCreationLogMetadata = BaseAuditLogType<
    'organization-note-creation',
    [UserReference, OrganizationReference, NoteReference]
>;

export type OrgNoteUpdateLogMetadata = BaseAuditLogType<
    'organization-note-update',
    [UserReference, OrganizationReference, NoteReference]
>;

export type OrgNoteDeleteLogMetadata = BaseAuditLogType<
    'organization-note-delete',
    [UserReference, OrganizationReference, NoteReference]
>;

export type AuditLogMetadata =
    | UserRegistrationLogMetadata
    | UserProfileViewLogMetadata
    | MedicalProfileCreationLogMetadata
    | MedicalProfileDeletionLogMetadata
    | CollaboratorAddLogMetadata
    | CollaboratorRemoveLogMetadata
    | CollaboratorInviteLogMetadata
    | CollaboratorInviteAcceptLogMetadata
    | CollaboratorInviteDeclineLogMetadata
    | CareTeamMemberCollaboratorAddLogMetadata
    | CareTeamMemberCollaboratorRemoveLogMetadata
    | ResourceCreationLogMetadata
    | ResourceUpdateLogMetadata
    | ResourceDeletionLogMetadata
    | NoteCreationLogMetadata
    | NoteMoveLogMetadata
    | NoteCommentLogMetadata
    | OneUpIntegrationAddLogMetadata
    | OneUpIntegrationRefreshLogMetadata
    | OneUpIntegrationRemoveLogMetadata
    | FastenIntegrationAddLogMetadata
    | FastenIntegrationRemoveLogMetadata
    | FastenIntegrationRefreshLogMetadata
    | MedicalProfileOwnershipTransferLogMetadata
    | SharedReportCreationLogMetadata
    | SharedReportDeletionLogMetadata
    | SharedReportViewedLogMetadata
    | PortalRefreshCompletionLogMetadata
    | ProfileMessageLogMetadata
    | MedicalProfileFileUploadLogMetadata
    | MedicalProfileFileDeleteLogMetadata
    | CodeStatusDirectiveCreationLogMetadata
    | CodeStatusDirectiveUpdateLogMetadata
    | POADirectiveCreationLogMetadata
    | POADirectiveUpdateLogMetadata
    | NoKnownAllergiesIndicatedLogMetadata
    | OrganizationCollaboratorAddLogMetadata
    | OrganizationCollaboratorRemoveLogMetadata
    | OrganizationInvoiceCreationLogMetadata
    | OrganizationPubliclyVisibleStatusUpdateLogMetadata
    | OrganizationSessionTimeoutUpdateLogMetadata
    | OrganizationPaidStatusUpdateLogMetadata
    | OrgUserCreationLogMetadata
    | OrgUserCreationOnInviteAcceptLogMetadata
    | OrgOwnerRoleAssignmentLogMetadata
    | OrgUserRoleAssignmentLogMetadata
    | OrgUserUpdateLogMetadata
    | OrgUserDeleteLogMetadata
    | OrgUserGroupAddLogMetadata
    | OrgUserGroupRemoveLogMetadata
    | OrgUserGroupAddBulkLogMetadata
    | OrgUserGroupRemoveBulkLogMetadata
    | OrgCareTeamMemberAddLogMetadata
    | OrgCareTeamMemberUpdateLogMetadata
    | OrgCareTeamMemberDeleteLogMetadata
    | OrgNoteCreationLogMetadata
    | OrgNoteUpdateLogMetadata
    | OrgNoteDeleteLogMetadata;

export type AuditLogTypes = AuditLogMetadata['type'];

/**
 * When audit logs are sent to the logging service, the references are not yet resolved.
 *
 * The audit log service needs to be able to resolve references for the initial write but also any refreshes
 */
export type UnresolvedAuditLogMetadata<TAuditLogMetadata extends AuditLogMetadata> =
    UnresolvedAuditLog<TAuditLogMetadata>;

export const potentialOrganizationUserAuditLogTypes = [
    'resource-creation',
    'resource-update',
    'resource-delete',
    'note-creation',
    'note-move',
    'note-comment',
    '1up-integration-add',
    '1up-integration-refresh',
    'shared-report-creation',
    'shared-report-deletion',
    'profile-message',
    'medical-profile-file-upload',
    'medical-profile-file-delete',
    'organization-invoice-creation',
    'code-status-directive-creation',
    'code-status-directive-update',
    'poa-directive-creation',
    'poa-directive-update',
] as const;
export type PotentialOrganizationUserAuditLogType = (typeof potentialOrganizationUserAuditLogTypes)[number];

type ExtractPotentialOrganizationUserAuditLogTypes<T, U> = T extends { type: U } ? T : never;
export type AuditLogMetadataWithPotentialOrganizationUser = ExtractPotentialOrganizationUserAuditLogTypes<
    AuditLogMetadata,
    PotentialOrganizationUserAuditLogType
>;
export type AuditLogMetadataWithPotentialOrganizationUserForType<TType extends PotentialOrganizationUserAuditLogType> =
    Extract<AuditLogMetadataWithPotentialOrganizationUser, { type: TType }>;

export const organizationEntityAuditLogTypes = [
    'organization-publicly-visible-status-update',
    'organization-session-timeout-update',
    'organization-paid-status-update',
    'organization-user-creation',
    'organization-user-creation-on-invite-accept',
    'organization-owner-role-assignment',
    'organization-user-role-assignment',
    'organization-user-update',
    'organization-user-delete',
    'organization-user-group-add',
    'organization-user-group-remove',
    'organization-user-group-add-bulk',
    'organization-user-group-remove-bulk',
    'organization-care-team-member-add',
    'organization-care-team-member-update',
    'organization-care-team-member-delete',
    'organization-note-creation',
    'organization-note-update',
    'organization-note-delete',
] as const;

export type OrganizationEntityAuditLogType = (typeof organizationEntityAuditLogTypes)[number];
